import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, ArrowLeft, AlertCircle, KeyRound, Copy, Check } from "lucide-react";

const errOf = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

function Enrol({ setup }) {
  const [copied, setCopied] = useState(false);
  if (!setup) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  const copy = () => { navigator.clipboard?.writeText(setup.secret); setCopied(true); };
  return (
    <div className="space-y-3" data-testid="mfa-enrol">
      <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
        <li>Open an authenticator app such as Google Authenticator or Microsoft Authenticator.</li>
        <li>Scan this QR code, or enter the setup key by hand.</li>
        <li>Type the 6-digit code the app shows.</li>
      </ol>
      <img src={setup.qr} alt="Authenticator QR code" className="mx-auto w-44 h-44 rounded-xl ring-1 ring-slate-200" data-testid="mfa-qr" />
      <button type="button" onClick={copy} className="w-full flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-left" data-testid="mfa-secret">
        <span className="mono text-xs tracking-wider text-slate-700 break-all">{setup.secret}</span>
        {copied ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : <Copy className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
    </div>
  );
}

export function MfaStep({ challenge, onDone, onBack }) {
  const enrolling = !challenge.enrolled;
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (enrolling) {
      api.post("/auth/mfa/setup", { mfa_token: challenge.mfa_token }).then((r) => setSetup(r.data)).catch((e) => setError(errOf(e)));
    }
  }, [enrolling, challenge.mfa_token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: challenge.mfa_token, code });
      onDone(data);
    } catch (err) {
      setError(errOf(err));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-step">
      <div className="flex items-start gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 shrink-0"><ShieldCheck className="w-5 h-5" /></span>
        <div>
          <h2 className="text-base font-bold text-slate-900">{enrolling ? "Set up your authenticator" : backup ? "Enter a backup code" : "Enter your authenticator code"}</h2>
          <p className="text-sm text-slate-500">{enrolling ? "This is needed once, then you'll enter a code every time you sign in." : backup ? "Enter one of your saved backup codes." : "Open your authenticator app and enter the 6-digit code."}</p>
        </div>
      </div>
      {enrolling && <Enrol setup={setup} />}
      <div className="relative">
        <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
        <input value={code} onChange={(e) => setCode(e.target.value)} autoFocus required
          inputMode={backup ? "text" : "numeric"} autoComplete="one-time-code" placeholder={backup ? "XXXX-XXXX" : "123456"}
          maxLength={backup ? 9 : 6} data-testid="mfa-code-input"
          className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-11 pr-3 text-lg tracking-[0.3em] mono text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
      </div>
      {error && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="mfa-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}
      <button type="submit" disabled={loading || (enrolling && !setup)} data-testid="mfa-submit"
        className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/25 transition-colors disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : enrolling ? "Confirm and sign in" : "Verify and sign in"}
      </button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-slate-500 hover:text-slate-700" data-testid="mfa-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        {!enrolling && (
          <button type="button" onClick={() => { setBackup((v) => !v); setCode(""); setError(""); }} className="font-semibold text-indigo-600 hover:text-indigo-700" data-testid="mfa-toggle-backup">
            {backup ? "Use authenticator code" : "Use a backup code"}
          </button>
        )}
      </div>
    </form>
  );
}

export function BackupCodes({ codes, onContinue }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(codes.join("\n")); setCopied(true); };
  return (
    <div className="space-y-5" data-testid="mfa-backup-codes">
      <div>
        <h2 className="text-base font-bold text-slate-900">Save your backup codes</h2>
        <p className="text-sm text-slate-500">If you lose your phone, each code lets you sign in once. Keep them somewhere safe. They won't be shown again.</p>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 border border-slate-200 p-4">
        {codes.map((c) => <span key={c} className="mono text-sm text-slate-800 text-center" data-testid="mfa-backup-code">{c}</span>)}
      </div>
      <button type="button" onClick={copy} className="w-full h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2" data-testid="mfa-backup-copy">
        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy codes"}
      </button>
      <button type="button" onClick={onContinue} data-testid="mfa-backup-continue"
        className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold shadow-lg shadow-indigo-600/25">
        I've saved these codes
      </button>
    </div>
  );
}
